import * as React from 'react';
import {
  Box,
  Icon,
  InputStatusMessage,
  Spinner,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { Tag, CopyButton } from '@web-config-app/core-react-ui';
import {
  useConfigApp,
  useLocales,
  useSetEntityData,
  useImagePreviewActions,
  useConfigFileUpload,
  useEntityData,
} from '@web-config-app/core-react';
import {
  EntityFormControl,
  useEntityFormControl,
  type ControlComponent,
  type EntityFormControlProps,
  useEntityDetailsProps,
  composeParentPath,
} from '@web-config-app/entity-form';
import { useIntl } from '@leagueplatform/locales';
import { get } from 'lodash-es';
import { FileTypeOptions } from '@web-config-app/core';
import { FileUpload } from '../upload-asset-modal/file-upload.component';
import { ChangeAssetActions } from './change-asset-actions.component';
import { ChangeAssetModal } from './change-asset-modal.component';

/**
 * AssetFileControl uploads the selected file immediately to /v1/config-file-upload and stores
 * the returned fileUploadId (and contentType) to the active locale.
 */

type FileType = FileTypeOptions['fileType'];

const previewCss = {
  maxWidth: '100%',
  width: 'auto',
  height: 'auto',
  borderRadius: '$medium',
};

const ellipsisTextCssBase = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: '1 1 0%',
  minWidth: 0,
};

const PdfIcon: React.FC = () => (
  <Box css={{ paddingInlineEnd: '$half' }}>
    <Icon
      tint="$onSurfaceIconPrimary"
      icon="pdf"
      size="$oneAndHalf"
      css={{ flexShrink: 0 }}
    />
  </Box>
);

const AssetFileControlContents = ({
  enabled,
  localized,
  path,
  schema,
}: EntityFormControlProps) => {
  const { formatMessage } = useIntl();
  const { currentLocale, hasMultipleLocales } = useLocales();
  const { defaultLocale } = useConfigApp();
  const entityData = useEntityData();
  const setEntityData = useSetEntityData();
  const { setPreviewUrl } = useImagePreviewActions();
  const { formPath } = useEntityDetailsProps();
  const {
    upload,
    isLoading: isUploading,
    isError,
    reset,
  } = useConfigFileUpload();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [fileError, setFileError] = React.useState<Error | null>(null);
  // Track when a file has been selected to prevent flash of FileUpload
  // after upload completes but before pendingFile is set
  const [hasSelectedFile, setHasSelectedFile] = React.useState(false);

  // Maintain pending selections per-locale so blob previews are scoped to the active locale only
  const [pendingByLocale, setPendingByLocale] = React.useState<
    Record<string, File | null>
  >({});
  const [objectUrlByLocale, setObjectUrlByLocale] = React.useState<
    Record<string, string | null>
  >({});

  const pendingFile = pendingByLocale[currentLocale] ?? null;
  const objectUrl = objectUrlByLocale[currentLocale] ?? null;
  // `path` points at a field within an asset object (e.g."image.url").
  // We need the parent object path so we can read/write sibling fields (url/fileName/fileUploadId/contentType).
  const parentPath = path.split('.').slice(0, -1).join('.');
  const normalizedFormPath = formPath && formPath !== 'root' ? formPath : '';
  // Make sure sibling fields (contentType/fileUploadId/fileName) are written
  // inside the same object as the field this control is bound to (via the schema
  // field annotated with x-entity-control.type === assetFile)
  const composedParentPath = React.useMemo(
    () => composeParentPath(normalizedFormPath, parentPath),
    [normalizedFormPath, parentPath],
  );

  // Helper to prefix any sibling field with the composed parent path.
  const buildPathWithParent = React.useCallback(
    (suffix: string) => [composedParentPath, suffix].filter(Boolean).join('.'),
    [composedParentPath],
  );

  const previewPath = React.useMemo(
    // the preview path needs to be keyed off the sibling `url` field,
    // even if this control is bound to a different sibling (e.g. `fileName`).
    () => `domainEntityAttributes.${buildPathWithParent('url')}`,
    [buildPathWithParent],
  );

  // Keep draft previews across locale switches, but reset non-per-locale transient UI state
  // (upload errors/loading, file validation errors, open modals) so it doesn't bleed
  // across locales.
  React.useEffect(() => {
    setIsModalOpen(false);
    setFileError(null);
    setHasSelectedFile(false);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocale]);

  // Reset hasSelectedFile when an error occurs so the user can retry
  React.useEffect(() => {
    if (isError) {
      setHasSelectedFile(false);
    }
  }, [isError]);

  // Reset hasSelectedFile when pendingFile is set (upload succeeded and component will switch view)
  React.useEffect(() => {
    if (pendingFile) {
      setHasSelectedFile(false);
    }
  }, [pendingFile]);

  React.useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setObjectUrlByLocale((prev) => {
        const previousUrl = prev[currentLocale];
        // Revoke any previous object URL for this locale to avoid leaks
        if (previousUrl) {
          try {
            URL.revokeObjectURL(previousUrl);
          } catch (_e) {
            // noop
          }
        }
        return { ...prev, [currentLocale]: url };
      });
      // Store preview URL in UI-only preview cache for the active locale
      setPreviewUrl(currentLocale, previewPath, url);
      return undefined;
    }
    setObjectUrlByLocale((prev) => ({ ...prev, [currentLocale]: null }));
    return undefined;
  }, [pendingFile, currentLocale, setPreviewUrl, previewPath]);

  const showLocaleBadge = Boolean(localized && hasMultipleLocales);

  const controlType = schema['x-entity-control'];

  const fileType = controlType?.options?.file?.fileType;
  if (!fileType) {
    throw new Error('AssetFileControl: missing file type');
  }

  const assetSettingsByType: Record<
    FileType,
    {
      Icon?: React.FC;
      changeLabelId: 'CHANGE_FILE' | 'CHANGE_IMAGE';
      showPreview: boolean;
      extensions: string[];
    }
  > = {
    pdf: {
      Icon: PdfIcon,
      changeLabelId: 'CHANGE_FILE',
      showPreview: false,
      extensions: ['pdf'],
    },
    image: {
      Icon: undefined,
      changeLabelId: 'CHANGE_IMAGE',
      showPreview: true,
      extensions: ['png', 'jpeg', 'jpg'],
    },
  };
  const presentation = assetSettingsByType[fileType];

  const accepts = presentation.extensions
    .map((extension) => `.${extension}`)
    .join(',');

  const uploadFile = React.useCallback(
    async (file: File) => upload(file, fileType),
    [upload, fileType],
  );

  const isLocalizedActive = localized && currentLocale !== defaultLocale;

  /**
   * When the control is bound to a non-URL field (e.g. `fileName`), we still need
   * to read the persisted asset URL from the entity data so we can display and
   * copy it.
   */
  const persistedUrl = React.useMemo(() => {
    const getUrlValue = (maybeUrl: unknown): string | undefined =>
      typeof maybeUrl === 'string' ? maybeUrl : undefined;

    const attributes = entityData?.attributes;
    if (!attributes) return undefined;

    const urlPath = buildPathWithParent('url');

    if (isLocalizedActive) {
      const localizedAttrs =
        attributes.translations?.[currentLocale]?.domainEntityAttributes ??
        attributes.translations?.[currentLocale];
      return getUrlValue(get(localizedAttrs, urlPath));
    }

    const defaultAttrs = attributes.domainEntityAttributes ?? attributes;
    return getUrlValue(get(defaultAttrs, urlPath));
  }, [entityData, isLocalizedActive, currentLocale, buildPathWithParent]);

  // Track previous persistedUrl per-locale to detect when it changes after publish.
  const prevPersistedUrlByLocaleRef = React.useRef<
    Record<string, string | undefined>
  >({});

  React.useEffect(() => {
    // If persistedUrl changed while we have a pendingFile, the publish completed
    // and we should clear the pending file to show the new URL
    const hasPrevForLocale = Object.prototype.hasOwnProperty.call(
      prevPersistedUrlByLocaleRef.current,
      currentLocale,
    );
    const prevForLocale = prevPersistedUrlByLocaleRef.current[currentLocale];

    // First observation for this locale: initialize and don't treat as a change.
    // This avoids clearing pending selections if the user selects a file before
    // this effect has ever run for the locale.
    if (!hasPrevForLocale) {
      prevPersistedUrlByLocaleRef.current[currentLocale] = persistedUrl;
      return;
    }

    if (pendingFile && persistedUrl !== prevForLocale) {
      setPendingByLocale((prev) => ({ ...prev, [currentLocale]: null }));
    }
    prevPersistedUrlByLocaleRef.current[currentLocale] = persistedUrl;
  }, [persistedUrl, pendingFile, currentLocale]);

  const setLocalizedEntityData = React.useCallback(
    (relativePath: string, value: any) => {
      const fullPath = buildPathWithParent(relativePath);

      if (isLocalizedActive) {
        setEntityData(
          value,
          `domainEntityAttributes.${fullPath}`,
          currentLocale,
        );
      } else {
        setEntityData(value, `attributes.domainEntityAttributes.${fullPath}`);
      }
    },
    [isLocalizedActive, currentLocale, setEntityData, buildPathWithParent],
  );

  const handleSelectFile = React.useCallback(
    async (file: File) => {
      // Clear any previous file validation error when a new file is selected
      setFileError(null);
      setHasSelectedFile(true);
      try {
        const fileUploadId = await uploadFile(file);

        if (file?.type) {
          setLocalizedEntityData('contentType', file.type);
        }

        if (fileUploadId) {
          setLocalizedEntityData('fileUploadId', fileUploadId);
          setLocalizedEntityData('fileName', file.name);
          setPendingByLocale((prev) => ({
            ...prev,
            [currentLocale]: file,
          }));
        }
        setIsModalOpen(false);
        reset();
      } catch (e: any) {
        console.error('Failed to upload file', e);
        setHasSelectedFile(false);
      }
    },
    [uploadFile, reset, setLocalizedEntityData, currentLocale],
  );

  if (pendingFile && objectUrl) {
    return (
      <StackLayout spacing="$half">
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="start"
          verticalAlignment="center"
          css={{ width: '100%' }}
        >
          {presentation.Icon && <presentation.Icon />}
          <UtilityText emphasis="emphasized" css={ellipsisTextCssBase}>
            {pendingFile.name}
          </UtilityText>
          <Box css={{ marginInlineStart: 'auto', flexShrink: 0 }}>
            <ChangeAssetActions
              onChange={() => setIsModalOpen(true)}
              showLocaleBadge={showLocaleBadge}
              locale={currentLocale}
              label={formatMessage({ id: presentation.changeLabelId })}
            />
          </Box>
        </StackLayout>
        {presentation.showPreview && (
          <Box as="img" src={objectUrl} alt="" css={previewCss} />
        )}
        <ChangeAssetModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onSelectFile={handleSelectFile}
          enabled={Boolean(enabled && !isUploading)}
          error={isError}
          setError={(value: boolean) => {
            if (!value) reset();
          }}
          isLoading={isUploading}
          accepts={accepts}
          modalTitle={formatMessage({ id: presentation.changeLabelId })}
        />
      </StackLayout>
    );
  }

  // When there is a persisted asset URL, show the existing asset with a link and preview.
  if (persistedUrl) {
    const urlString = persistedUrl;

    return (
      <StackLayout>
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="start"
          verticalAlignment="center"
          css={{ width: '100%' }}
        >
          {presentation.Icon && <presentation.Icon />}
          <UtilityText
            as="a"
            href={urlString}
            target="_blank"
            rel="noopener noreferrer"
            css={{
              wordBreak: 'break-all',
              typography: '$caption',
              color: '$interactiveActionPrimary',
              ...ellipsisTextCssBase,
            }}
          >
            {urlString}
          </UtilityText>
          <StackLayout
            orientation="horizontal"
            spacing="$half"
            verticalAlignment="center"
            horizontalAlignment="end"
            css={{ marginInlineStart: 'auto', flex: '0 0 auto' }}
          >
            <Box css={{ flex: '0 0 auto' }}>
              <CopyButton
                label="Copy"
                priority="tertiary"
                contents={urlString ?? ''}
                hideLabel
              />
            </Box>
            <ChangeAssetActions
              onChange={() => setIsModalOpen(true)}
              showLocaleBadge={showLocaleBadge}
              locale={currentLocale}
              label={formatMessage({ id: presentation.changeLabelId })}
            />
          </StackLayout>
        </StackLayout>
        {presentation.showPreview && urlString && (
          <Box as="img" src={urlString} alt="" css={previewCss} />
        )}
        <ChangeAssetModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onSelectFile={handleSelectFile}
          enabled={Boolean(enabled && !isUploading)}
          error={isError}
          setError={(value: boolean) => {
            if (!value) reset();
          }}
          isLoading={isUploading}
          accepts={accepts}
          modalTitle={formatMessage({ id: presentation.changeLabelId })}
        />
      </StackLayout>
    );
  }

  return (
    <Box css={{ position: 'relative', width: '100%' }}>
      {isUploading || hasSelectedFile ? (
        <StackLayout css={{ width: '100%' }} horizontalAlignment="center">
          <Spinner loading />
        </StackLayout>
      ) : (
        <FileUpload
          file={null}
          onSelect={handleSelectFile}
          onError={setFileError}
          disabled={!enabled}
          accepts={accepts}
        />
      )}
      {(fileError || isError) && (
        <InputStatusMessage inputStatus="error">
          {fileError?.message ?? formatMessage({ id: 'UPLOAD_FILE_ERROR' })}
        </InputStatusMessage>
      )}
      {showLocaleBadge && (
        <Box
          css={{
            position: 'absolute',
            insetInlineEnd: '$one',
            top: '$twoAndHalf',
            pointerEvents: 'none',
          }}
        >
          <Tag label={currentLocale} />
        </Box>
      )}
    </Box>
  );
};

export const AssetFileControl: ControlComponent = (props) => {
  const { id } = useEntityFormControl(props);
  return (
    <EntityFormControl
      key={id}
      {...props}
      renderControl={AssetFileControlContents}
    />
  );
};
